import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Container,
  Grid,
} from "@mui/material";
import {
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarIcon,
  Visibility as EyeIcon,
  VisibilityOff as EyeOffIcon,
  Shield as ShieldIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Lock as LockIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import moment from "moment";

const CardDetails = () => {
  const [cardNumber, setCardNumber] = useState("");
  const [cardHolder, setCardHolder] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [address, setAddress] = useState("");
  const [showCvv, setShowCvv] = useState(false);
  const [errors, setErrors] = useState({});
  const [cardType, setCardType] = useState(null);
  const [selectedBillingCycle] = useState("monthly");

  // Mock plan data
  const planData = {
    monthly: {
      name: "Professional Plan",
      price: 29.99,
      interval: "month",
      features: ["Unlimited projects", "Priority support", "Advanced analytics", "Custom integrations"],
    },
    yearly: {
      name: "Professional Plan",
      price: 299.99,
      interval: "year",
      savings: "Save $60/year",
      features: ["Unlimited projects", "Priority support", "Advanced analytics", "Custom integrations"],
    },
  };

  const currentPlan = planData[selectedBillingCycle];

  // Format card number with spaces
  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, "");
    const match = cleaned.match(/.{1,4}/g);
    return match ? match.join(" ") : cleaned;
  };

  // Format expiry date as MM/YY
  const formatExpiryDate = (value) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length >= 2) {
      return cleaned.slice(0, 2) + "/" + cleaned.slice(2, 4);
    }
    return cleaned;
  };

  // Detect card type
  const detectCardType = (number) => {
    const cleaned = number.replace(/\s/g, "");
    if (/^4/.test(cleaned)) return "visa";
    if (/^5[1-5]/.test(cleaned)) return "mastercard";
    if (/^3[47]/.test(cleaned)) return "amex";
    if (/^6/.test(cleaned)) return "discover";
    return null;
  };

  // Get card type display info
  const getCardTypeDisplay = () => {
    const types = {
      visa: { name: "Visa", color: "#1e40af" },
      mastercard: { name: "Mastercard", color: "#D32B2B" },
      amex: { name: "Amex", color: "#16a34a" },
      discover: { name: "Discover", color: "#f97316" },
    };
    return types[cardType] || null;
  };

  // Get card gradient
  const getCardGradient = () => {
    if (!cardType) return "linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)";

    const gradients = {
      visa: "linear-gradient(135deg, #2563eb 0%, #1e40af 50%, #1e3a8a 100%)",
      mastercard: "linear-gradient(135deg, #ea580c 0%, #dc2626 50%, #b91c1c 100%)",
      amex: "linear-gradient(135deg, #16a34a 0%, #15803d 50%, #166534 100%)",
      discover: "linear-gradient(135deg, #f97316 0%, #ea580c 50%, #c2410c 100%)",
    };
    return gradients[cardType] || "linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)";
  };

  // Validation functions
  const validateCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, "");
    if (!cleaned) return "Card number is required";
    if (cardType === "amex" && cleaned.length !== 15) {
      return "American Express cards must have 15 digits";
    }
    if (cardType !== "amex" && cleaned.length !== 16) {
      return "Card number must have 16 digits";
    }
    // Luhn algorithm check
    let sum = 0;
    let isEven = false;
    for (let i = cleaned.length - 1; i >= 0; i--) {
      let digit = parseInt(cleaned[i]);
      if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    if (sum % 10 !== 0) return "Invalid card number";
    return "";
  };

  const validateCardHolder = (value) => {
    if (!value.trim()) return "Card holder name is required";
    if (value.trim().length < 2) return "Name must be at least 2 characters";
    return "";
  };

  const validateExpiryDate = (value) => {
    if (!value) return "Expiry date is required";
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length !== 4) return "Invalid expiry date";
    const month = parseInt(cleaned.slice(0, 2));
    const year = parseInt("20" + cleaned.slice(2, 4));
    if (month < 1 || month > 12) return "Invalid month";
    const expiry = moment(`${year}-${month.toString().padStart(2, "0")}-01`);
    if (expiry.isBefore(moment(), "month")) return "Card has expired";
    return "";
  };

  const validateCVV = (value) => {
    if (!value) return "CVV is required";
    const maxLength = cardType === "amex" ? 4 : 3;
    if (value.length !== maxLength) {
      return cardType === "amex" ? "CVV must be 4 digits" : "CVV must be 3 digits";
    }
    return "";
  };

  const validateZipCode = (value) => {
    if (!value.trim()) return "Zip code is required";
    const cleaned = value.trim().replace(/\D/g, "");
    if (cleaned.length !== 5) return "Zip code must be 5 digits";
    return "";
  };

  const validateAddress = (value) => {
    if (!value.trim()) return "Address is required";
    if (value.trim().length < 5) return "Address must be at least 5 characters";
    return "";
  };

  // Get next billing date
  const getNextBillingDate = () => {
    const date = moment();
    if (selectedBillingCycle === "yearly") {
      return date.add(1, "year").format("MMMM DD, YYYY");
    } else {
      return date.add(1, "month").format("MMMM DD, YYYY");
    }
  };

  // Handle card number change with type detection
  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    const cleaned = formatted.replace(/\s/g, "");
    const maxLength = cardType === "amex" ? 15 : 16;

    if (cleaned.length <= maxLength) {
      setCardNumber(formatted);
      const detectedType = detectCardType(cleaned);
      setCardType(detectedType);
      if (errors.cardNumber) {
        setErrors({ ...errors, cardNumber: "" });
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: "linear-gradient(135deg, #9333ea 0%, #7c3aed 50%, #6d28d9 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            mb: 1,
          }}
        >
          Secure Payment
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Complete your subscription with confidence
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Left Column - Payment Form */}
        <Grid item xs={12} md={7}>
          <Box sx={{ "& > * + *": { mt: 3 } }}>
            {/* Payment Form Card */}
            <Card
              sx={{
                border: "1px solid",
                borderColor: "divider",
                boxShadow: 2,
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ "& > * + *": { mt: 3 } }}>
                  {/* Card Number */}
                  <Box sx={{ "& > * + *": { mt: 1 } }}>
                    <Typography
                      component="label"
                      htmlFor="card-number"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        display: "block",
                        mb: 1,
                      }}
                    >
                      Card Number
                    </Typography>
                    <Box sx={{ position: "relative" }}>
                      <TextField
                        id="card-number"
                        type="text"
                        inputMode="numeric"
                        value={cardNumber}
                        onChange={handleCardNumberChange}
                        onBlur={() => {
                          const error = validateCardNumber(cardNumber);
                          setErrors({ ...errors, cardNumber: error });
                        }}
                        placeholder="1234 5678 9012 3456"
                        fullWidth
                        error={!!errors.cardNumber}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CreditCardIcon sx={{ color: "text.secondary" }} />
                            </InputAdornment>
                          ),
                          endAdornment: cardType && getCardTypeDisplay() ? (
                            <InputAdornment position="end">
                              <Chip
                                label={getCardTypeDisplay().name}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: "10px",
                                  fontWeight: 700,
                                  bgcolor: getCardTypeDisplay().color,
                                  color: "white",
                                  "& .MuiChip-label": {
                                    px: 1,
                                  },
                                }}
                              />
                            </InputAdornment>
                          ) : null,
                        }}
                      />
                    </Box>
                    {errors.cardNumber && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "error.main",
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <InfoIcon sx={{ fontSize: 12 }} />
                        {errors.cardNumber}
                      </Typography>
                    )}
                    {cardType && !errors.cardNumber && cardNumber.replace(/\s/g, "").length > 6 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "success.main",
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 12 }} />
                        {getCardTypeDisplay()?.name} detected
                      </Typography>
                    )}
                  </Box>

                  {/* Card Holder Name */}
                  <Box sx={{ "& > * + *": { mt: 1 } }}>
                    <Typography
                      component="label"
                      htmlFor="card-holder"
                      sx={{
                        fontSize: "0.875rem",
                        fontWeight: 600,
                        display: "block",
                        mb: 1,
                      }}
                    >
                      Card Holder Name
                    </Typography>
                    <TextField
                      id="card-holder"
                      type="text"
                      value={cardHolder}
                      onChange={(e) => {
                        setCardHolder(e.target.value.toUpperCase());
                        if (errors.cardHolder) {
                          setErrors({ ...errors, cardHolder: "" });
                        }
                      }}
                      onBlur={() => {
                        const error = validateCardHolder(cardHolder);
                        setErrors({ ...errors, cardHolder: error });
                      }}
                      placeholder="JOHN DOE"
                      fullWidth
                      error={!!errors.cardHolder}
                      inputProps={{ style: { textTransform: "uppercase" } }}
                    />
                    {errors.cardHolder && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "error.main",
                          fontSize: "0.75rem",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <InfoIcon sx={{ fontSize: 12 }} />
                        {errors.cardHolder}
                      </Typography>
                    )}
                  </Box>

                  {/* Expiry & CVV */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ "& > * + *": { mt: 1 } }}>
                        <Typography
                          component="label"
                          htmlFor="expiry"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Expiry Date
                        </Typography>
                        <TextField
                          id="expiry"
                          type="text"
                          value={expiryDate}
                          onChange={(e) => {
                            const formatted = formatExpiryDate(e.target.value);
                            if (formatted.replace(/\D/g, "").length <= 4) {
                              setExpiryDate(formatted);
                              if (errors.expiryDate) {
                                setErrors({ ...errors, expiryDate: "" });
                              }
                            }
                          }}
                          onBlur={() => {
                            const error = validateExpiryDate(expiryDate);
                            setErrors({ ...errors, expiryDate: error });
                          }}
                          placeholder="MM/YY"
                          fullWidth
                          error={!!errors.expiryDate}
                        />
                        {errors.expiryDate && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "error.main",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 12 }} />
                            {errors.expiryDate}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ "& > * + *": { mt: 1 } }}>
                        <Typography
                          component="label"
                          htmlFor="cvv"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          CVV {cardType === "amex" && "(4 digits)"}
                        </Typography>
                        <TextField
                          id="cvv"
                          type={showCvv ? "text" : "password"}
                          value={cvv}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            const maxLength = cardType === "amex" ? 4 : 3;
                            if (value.length <= maxLength) {
                              setCvv(value);
                              if (errors.cvv) {
                                setErrors({ ...errors, cvv: "" });
                              }
                            }
                          }}
                          onBlur={() => {
                            const error = validateCVV(cvv);
                            setErrors({ ...errors, cvv: error });
                          }}
                          placeholder={cardType === "amex" ? "1234" : "123"}
                          inputProps={{ maxLength: cardType === "amex" ? 4 : 3 }}
                          fullWidth
                          error={!!errors.cvv}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowCvv(!showCvv)}
                                  edge="end"
                                  tabIndex={-1}
                                  size="small"
                                >
                                  {showCvv ? <EyeOffIcon /> : <EyeIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {errors.cvv && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "error.main",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 12 }} />
                            {errors.cvv}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Zip Code & Address */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ "& > * + *": { mt: 1 } }}>
                        <Typography
                          component="label"
                          htmlFor="zipCode"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Zip Code
                        </Typography>
                        <TextField
                          id="zipCode"
                          type="text"
                          value={zipCode}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            if (value.length <= 5) {
                              setZipCode(value);
                              if (errors.zipCode && value.length === 5) {
                                setErrors({ ...errors, zipCode: "" });
                              }
                            }
                          }}
                          onBlur={() => {
                            const error = validateZipCode(zipCode);
                            setErrors({ ...errors, zipCode: error });
                          }}
                          placeholder="12345"
                          fullWidth
                          error={!!errors.zipCode}
                        />
                        {errors.zipCode && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "error.main",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 12 }} />
                            {errors.zipCode}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ "& > * + *": { mt: 1 } }}>
                        <Typography
                          component="label"
                          htmlFor="address"
                          sx={{
                            fontSize: "0.875rem",
                            fontWeight: 600,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Address
                        </Typography>
                        <TextField
                          id="address"
                          type="text"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                          }}
                          onBlur={() => {
                            const error = validateAddress(address);
                            setErrors({ ...errors, address: error });
                          }}
                          placeholder="123 Main St"
                          fullWidth
                          error={!!errors.address}
                        />
                        {errors.address && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "error.main",
                              fontSize: "0.75rem",
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 12 }} />
                            {errors.address}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>

            {/* Security Badges */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 3,
                flexWrap: "wrap",
                color: "text.secondary",
                fontSize: "0.875rem",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockIcon sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="body2">256-bit SSL Encrypted</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <VerifiedIcon sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="body2">PCI-DSS Compliant</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShieldIcon sx={{ fontSize: 16, color: "success.main" }} />
                <Typography variant="body2">Powered by Plug'n Pay</Typography>
              </Box>
            </Box>
            {/* Security Info Card */}
            <Card sx={{ border: "1px solid", borderColor: "divider" }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <ShieldIcon sx={{ color: "primary.main", fontSize: 24, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Your payment is secure
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.75rem", lineHeight: 1.6 }}>
                      All transactions are encrypted and processed through our secure payment gateway. We never store your complete card details.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* Right Column - Card Preview & Plan Summary */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: "sticky", top: 20, "& > * + *": { mt: 3 } }}>
            {/* Visual Card Preview */}
            <Card
              sx={{
                background: getCardGradient(),
                color: "white",
                borderRadius: 3,
                boxShadow: 4,
                minHeight: 220,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 100%)",
                }}
              />
              <CardContent sx={{ p: 3, position: "relative", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      width: 48,
                      height: 36,
                      background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                      borderRadius: 1,
                      opacity: 0.9,
                    }}
                  />
                  {cardType && getCardTypeDisplay() && (
                    <Chip
                      label={getCardTypeDisplay().name}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        backdropFilter: "blur(10px)",
                        color: "white",
                        fontWeight: 700,
                        fontSize: "10px",
                      }}
                    />
                  )}
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "monospace",
                      letterSpacing: 2,
                      mb: 3,
                      fontSize: "1.5rem",
                    }}
                  >
                    {cardNumber || "•••• •••• •••• ••••"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.7rem" }}>
                      CARD HOLDER
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      {cardHolder || "YOUR NAME"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.7, fontSize: "0.7rem" }}>
                      EXPIRES
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      {expiryDate || "MM/YY"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Plan Summary Card */}
            <Card
              sx={{
                background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 50%, #4c1d95 100%)",
                color: "white",
                borderRadius: 3,
                boxShadow: 4,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ "& > * + *": { mt: 3 } }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.8)",
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        letterSpacing: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      YOUR PLAN
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: "#fff" }}>
                      {currentPlan.name}
                    </Typography>
                    {selectedBillingCycle === "yearly" && currentPlan.savings && (
                      <Chip
                        label={currentPlan.savings}
                        size="small"
                        sx={{
                          mt: 1,
                          bgcolor: "rgba(255,255,255,0.2)",
                          backdropFilter: "blur(10px)",
                          color: "white",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      borderTop: "1px solid rgba(255, 255, 255, 0.2)",
                      borderBottom: "1px solid rgba(255,255,255,0.2)",
                      py: 2,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: "#fff" }}>
                        ${currentPlan.price}
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9, color: "#fff" }}>
                        /{currentPlan.interval}
                      </Typography>
                    </Box>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255,255,255,0.9)",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        letterSpacing: 0.5,
                        mb: 1.5,
                        display: "block",
                      }}
                    >
                      INCLUDED FEATURES
                    </Typography>
                    <Box sx={{ "& > * + *": { mt: 1.5 } }}>
                      {currentPlan.features.map((feature, index) => (
                        <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <CheckIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Card
                    sx={{
                      bgcolor: "rgba(255,255,255,0.1)",
                      backdropFilter: "blur(10px)",
                      borderRadius: 2,
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: "0.875rem", color: "#fff" }}>
                          Next Billing Date
                        </Typography>
                        <CalendarIcon sx={{ fontSize: 18, opacity: 0.8, color: "#fff" }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5, color: "#fff" }}>
                        {getNextBillingDate()}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8, fontSize: "0.7rem", color: "#fff" }}>
                        Auto-renews {selectedBillingCycle === "yearly" ? "annually" : "monthly"}
                      </Typography>
                    </CardContent>
                  </Card>

                  <Box
                    sx={{
                      bgcolor: "rgba(0,0,0,0.2)",
                      borderRadius: 2,
                      p: 1.5,
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <InfoIcon sx={{ fontSize: 16, flexShrink: 0, mt: 0.25 }} />
                    <Typography variant="caption" sx={{ fontSize: "0.7rem", lineHeight: 1.6 }}>
                      You can cancel anytime. No hidden fees or commitments.
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CardDetails;
